interface ConfirmModalOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  confirmClass?: string;
  icon?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

class ConfirmModal {
  private modal: HTMLDivElement | null = null;
  private settle: ((confirmed: boolean) => void) | null = null;

  public show(options: ConfirmModalOptions): Promise<boolean> {
    // A dialog still open is superseded as cancelled so its elements and promise never leak.
    this.close();

    return new Promise((resolve) => {
      const {
        title = 'Confirm Action',
        message = 'Are you sure you want to proceed?',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        confirmClass = 'btn-danger',
        icon = 'ti ti-alert-triangle',
      } = options;

      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop fade';
      document.body.appendChild(backdrop);

      const modal = document.createElement('div');
      modal.className = 'modal modal-blur fade';
      modal.setAttribute('tabindex', '-1');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.style.display = 'block';
      this.modal = modal;

      modal.innerHTML = `
        <div class="modal-dialog modal-sm modal-dialog-centered" role="document">
          <div class="modal-content">
            <button type="button" class="btn-close" data-dismiss="modal" aria-label="Close"></button>
            <div class="modal-body text-center py-4">
              <i class="${icon} icon mb-2 text-danger icon-lg"></i>
              <h3>${title}</h3>
              <div class="text-secondary">${message}</div>
            </div>
            <div class="modal-footer">
              <div class="w-100">
                <div class="row">
                  <div class="col">
                    <button type="button" class="btn w-100" data-action="cancel" data-cy="confirm-modal-cancel">
                      ${cancelText}
                    </button>
                  </div>
                  <div class="col">
                    <button type="button" class="btn ${confirmClass} w-100" data-action="confirm" data-cy="confirm-modal-confirm">
                      ${confirmText}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          settle(false);
        }
      };

      const settle = (confirmed: boolean): void => {
        if (this.settle !== settle) {
          return;
        }
        this.settle = null;
        document.removeEventListener('keydown', handleEscape);
        (confirmed ? options.onConfirm : options.onCancel)?.();
        this.dismiss(modal, backdrop);
        resolve(confirmed);
      };
      this.settle = settle;

      modal.querySelector('[data-action="confirm"]')?.addEventListener('click', () => settle(true));
      modal.querySelector('[data-action="cancel"]')?.addEventListener('click', () => settle(false));
      modal.querySelector('.btn-close')?.addEventListener('click', () => settle(false));
      backdrop.addEventListener('click', () => settle(false));
      document.addEventListener('keydown', handleEscape);

      // Moves focus off the trigger, so Enter confirms instead of re-clicking the trigger.
      modal.querySelector<HTMLButtonElement>('[data-action="confirm"]')?.focus();

      requestAnimationFrame(() => {
        if (this.settle !== settle) {
          return;
        }
        modal.classList.add('show');
        backdrop.classList.add('show');
        document.body.classList.add('modal-open');
      });
    });
  }

  public close(): void {
    this.settle?.(false);
  }

  private dismiss(modal: HTMLDivElement, backdrop: HTMLDivElement): void {
    modal.classList.remove('show');
    backdrop.classList.remove('show');

    if (this.modal === modal) {
      this.modal = null;
    }

    setTimeout(() => {
      modal.remove();
      backdrop.remove();
      if (this.modal === null) {
        document.body.classList.remove('modal-open');
      }
    }, 150); // Match Bootstrap's fade transition duration
  }
}

let confirmModalInstance: ConfirmModal | null = null;

function getConfirmModalInstance(): ConfirmModal {
  confirmModalInstance ??= new ConfirmModal();
  return confirmModalInstance;
}

export async function showConfirm(options: ConfirmModalOptions): Promise<boolean> {
  const modal = getConfirmModalInstance();
  return modal.show(options);
}

export async function showDeleteConfirm(message?: string): Promise<boolean> {
  return showConfirm({
    title: 'Confirm Deletion',
    message: message ?? 'Are you sure you want to delete the selected items? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    confirmClass: 'btn-danger',
    icon: 'ti ti-trash',
  });
}

export default ConfirmModal;
